import {
  getLocaleFromPathname,
  stripLocalePrefix,
} from "./localeRoutes";

const SITE_URL = "https://dociohealth.com";
const PREFIXED_LOCALES = ["tr", "de", "es", "ru", "el"] as const;

export interface SeoUrls {
  canonicalUrl: string;
  pageSlug: string;
  alternates: Array<{ locale: string; href: string }>;
}

function normalizePath(pathname: string): string {
  if (!pathname || pathname === "/") return "/";
  return `/${pathname.split("/").filter(Boolean).join("/")}`;
}

function localizedUrl(pageSlug: string, locale: string): string {
  const suffix = pageSlug === "/" ? "" : pageSlug;
  return locale === "en"
    ? `${SITE_URL}${pageSlug}`
    : `${SITE_URL}/${locale}${suffix}`;
}

export function buildSeoUrls(pathname: string): SeoUrls {
  const canonicalPath = normalizePath(pathname);
  const pageSlug = normalizePath(stripLocalePrefix(canonicalPath));
  const canonicalLocale = getLocaleFromPathname(canonicalPath);
  const alternates = [
    { locale: "en", href: localizedUrl(pageSlug, "en") },
    ...PREFIXED_LOCALES.map((locale) => ({
      locale,
      href: localizedUrl(pageSlug, locale),
    })),
    { locale: "x-default", href: localizedUrl(pageSlug, "en") },
  ];

  return {
    canonicalUrl: localizedUrl(pageSlug, canonicalLocale),
    pageSlug,
    alternates,
  };
}

export function normalizeSeoTitle(title: string): string {
  return /\bdocio(?: health)?\b/i.test(title)
    ? title
    : `${title} | Docio Health`;
}

export function normalizeMetaDescription(
  description: string,
  maxLength = 160,
): string {
  const compact = description.trim().replace(/\s+/g, " ");
  if (compact.length <= maxLength) return compact;

  const slice = compact.slice(0, maxLength - 1);
  const wordBoundary = slice.lastIndexOf(" ");
  const end = wordBoundary > 100 ? wordBoundary : slice.length;
  return `${slice.slice(0, end)}…`;
}

export function setHtmlLanguage(html: string, pathname: string): string {
  const locale = getLocaleFromPathname(pathname);
  return html.replace(
    /<html([^>]*)\slang="[^"]*"/,
    (match) => match.replace(/lang="[^"]*"/, `lang="${locale}"`),
  );
}
